import L from "leaflet";
import "leaflet-draw";
import Wkt from "wicket";
import "wicket/wicket-leaflet.js";
import "leaflet-draw-drag";

/**
 * @class AstroDrawControl
 * @aka L.Control.AstroDrawControl
 * @extends L.Control
 * @classdesc
 * Class that extends from the class L.Control.Draw and handles the back-end when a user draws on the leaflet map.
 * Since this class inherits L.Control, it is added to the AstroMap in the same way as other controls, like the zoom control.
 *
 * @example
 *
 * // add a feature group to the map
 * let drawnItems = new L.FeatureGroup();
 * map.addLayer(drawnItems);
 *
 * // add draw control to map
 * let drawControl = new AstroDrawControl({
 *   edit: {
 *      featureGroup: drawnItems
 *   }
 * }).addTo(map);
 */
export default L.Control.AstroDrawControl = L.Control.Draw.extend({
  options: {
    draw: {
      circle: false,
      marker: false,
      circlemarker: {
        color: "red",
        fillColor: "red",
        radius: 5
      }
    },
    edit: true,
    accuracy: 4
  },

  /**
   * @function AstroDrawControl.prototype.onAdd
   * @description Adds the draw control to the map provided. Creates an on-draw and on-click event
   *              that allows users to draw polygons onto the leaflet map.
   * @param  {AstroMap} map - The AstroMap to add the control to.
   * @return {Object} The div-container the control is in.
   */
  onAdd: function(map) {
    // Update circlemarker tooltip to say point
    L.drawLocal.draw.toolbar.buttons.circlemarker = "Draw a point";

    this._map = map;
    this.wkt = new Wkt.Wkt();
    this.myLayer = L.Proj.geoJson().addTo(map);

    map.on(
      "draw:created",
      function(e) {
        this.options.edit["featureGroup"].addLayer(e.layer);
        e.layer.on(
          "click",
          function(e) {
            this.updateWKT(e.target);

            // Easiest way to check type of shape,
            // since Circle inherits from CircleMarker,
            // check if not a Circle.
            if (
              e.target instanceof L.CircleMarker &&
              !(e.target instanceof L.Circle)
            ) {
              this.zoomOnShape(e.target, "Point");
            } else {
              this.zoomOnShape(e.target, "Polygon");
            }
          },
          this
        );
        this.updateWKT(e.layer);
      },
      this
    );
    map.on(
      "draw:edited",
      function(e) {
        let editedShape = e.layers._layers[Object.keys(e.layers._layers)[0]];
        if (editedShape != undefined) {
          this.updateWKT(editedShape);
        }
      },
      this
    );
    map.on(
      "draw:deleted",
      function(e) {
        // Send update to parent window
        window.top.postMessage("setWkt " + this.getCombinedWkt(), "*");
      },
      this
    );

    return L.Control.Draw.prototype.onAdd.call(this, map);
  },

  /**
   * @function AstroDrawControl.prototype.getCombinedWkt
   * @description Returns Well Known Text (WKT) with all features combined.
   */
  getCombinedWkt: function() {
    try {
      let combinedWkt = false;
      for (var layer of this.options.edit.featureGroup.getLayers()) {
        if (!combinedWkt) {
          combinedWkt = new Wkt.Wkt(this.geomTo360(layer.toGeoJSON().geometry));
        } else {
          combinedWkt.merge(new Wkt.Wkt(this.geomTo360(layer.toGeoJSON().geometry)));
        }
      }
      return this.trimWkt(combinedWkt.write());
    } catch (error) {
      if (error.message.includes("The input geometry types must agree or the calling this.Wkt.Wkt instance must be a multigeometry of the other")) {
        return "Unsupported: Cannot combine different types of geometry.";
      } else if (error.message.includes(".write is not a function")) {
        return "";
      } else {
        return error.message;
      }
    }
  },
  geomTo360(geometry) {
    let geom360 = structuredClone(geometry);
  
    // Normalize longitude to 0-360 with continuity handling
    let lonTo360 = (lon, prevLon) => {
      lon = (lon + 360) % 360; // Ensure 0-360 range
  
      // Handle discontinuity across 360 boundary
      if (prevLon !== null) {
        let diff = lon - prevLon;
        if (diff > 180) lon -= 360; // Large positive jump, adjust back
        else if (diff < -180) lon += 360; // Large negative jump, adjust forward
      }
      return lon;
    };
  
    // Process different geometry types
    if (geom360.type === "Polygon") {
      let prevLon = null;
      geom360.coordinates[0] = geometry.coordinates[0].map(c => {
        let lon = lonTo360(c[0], prevLon);
        prevLon = lon; 
        return [lon, c[1]];
      });
    }
    else if (geom360.type === "Point") {
      geom360.coordinates[0] = lonTo360(geom360.coordinates[0], null);
    }
    else if (geom360.type === "LineString") {
      let prevLon = null;
      geom360.coordinates = geometry.coordinates.map(c => {
        let lon = lonTo360(c[0], prevLon);
        prevLon = lon;
        return [lon, c[1]];
      });
    }
    else if (geom360.type === "MultiPolygon") {
      geom360.coordinates = geom360.coordinates.map(polygon => {
        let prevLon = null;
        return polygon[0].map(c => {
          let lon = lonTo360(c[0], prevLon);
          prevLon = lon;
          return [lon, c[1]];
        });
      });
    } 
    return geom360;
  }, 

  /**
   * @function AstroDrawControl.prototype.updateWKT
   * @description Updates the Well Known Text (WKT) box with the previously drawn shape.
   * @param  {L.Layer} shape - Leaflet layer describing the shape.
   */
  updateWKT: function(shape) {

    let geoJson = shape.toGeoJSON().geometry;

    let geoJson360 = this.geomTo360(geoJson);

    // GeoJSON does not support circles natively,
    // so approximate a circle with a polygon
    if (shape instanceof L.Circle) {
      geoJson360 = this.createGeoJSONCircle(
        geoJson360["coordinates"],
        shape._mRadius,
        50
      );
    }

    this.wkt.read(JSON.stringify(geoJson360));

    // React listens for this message and uses to update wkt textbox
    window.postMessage("setWkt " + this.trimWkt(this.wkt.write()), "*");

    // Send update to parent window
    window.top.postMessage("setWkt " + this.getCombinedWkt(), "*");
  },

  /**
   * @function AstroDrawControl.prototype.trimWkt
   * @description Zooms in on the shape depending on the type.
   * @param  {L.Layer} shape - Leaflet layer describing the shape.
   * @param  {String} type - Type of the shape.
   */
  trimWkt: function(wktStr) {
    try {
      this.wkt.read(wktStr);
    } catch (err) {
      console.log("Invalid Well Known Text String");
      return;
    }
    return wktStr.replace(/\d+\.\d+/g, match =>
      Number(match).toFixed(this.options.accuracy)
    );
  },

  /**
   * @function AstroDrawControl.prototype.zoomOnShape
   * @description Zooms in on the shape depending on the type.
   * @param  {L.Layer} shape - Leaflet layer describing the shape.
   * @param  {String} type - Type of the shape.
   */
  zoomOnShape: function(feature, type) {
    if (type == "Point") {
      this._map.setView(feature.getLatLng(), this._map.options.maxZoom);
    } else {
      this._map.fitBounds(feature.getBounds().pad(0.3));
    }
  },

  /**
   * @function AstroDrawControl.prototype.clearFeatures
   * @description Zooms in on the shape depending on the type.
   */
  clearFeatures: function() {
    this.options.edit.featureGroup.clearLayers();
  },

  /**
   * @function AstroDrawControl.prototype.addShapeFromWKT
   * @description  Creates a feature GeoJSON from an input WKT and adds the feature to
   *               the map.
   * @param  {String} wktStr  - Well-known text string of geometry.
   */
  addShapeFromWKT: function(wktStr) {
    wktStr = this.trimWkt(wktStr);
    try {
      this.wkt.read(wktStr);
    } catch (err) {
      alert("Invalid Well Known Text String");
      return;
    }

    let feature = this.wkt.toObject();
    let geoJson = this.wkt.toJson();

    // Had some problems drawing points, so manually add
    // the circle marker.
    let drawControl = this;
    if (geoJson["type"] == "Point") {
      feature = L.circleMarker(
        [geoJson["coordinates"][1], geoJson["coordinates"][0]],
        drawControl.options.draw.circlemarker
      );
    }

    this.zoomOnShape(feature, geoJson["type"]);
    this.options.edit["featureGroup"].clearLayers();
    this.options.edit["featureGroup"].addLayer(feature);

    feature.on(
      "click",
      function() {
        // Remove:
        this.updateWKT(feature);
        this.zoomOnShape(feature, geoJson["type"]);
      },
      this
    );
  },

  /**
   * @function AstroDrawControl.prototype.createGeoJSONCircle
   * @description  Approximates a circle with a GeoJSON polygon.
   * @param  {Array} center     - Center of the circle.
   * @param  {Float} radius     - Radius of the circle in meters.
   * @param  {Integer} points   - Number of points to fill the
   *                             polygon with.
   */
  createGeoJSONCircle: function(center, radius, points) {
    if (!points) points = 64;

    let coords = {
      latitude: center[1],
      longitude: center[0]
    };

    let km = radius / 1000.0;

    let ret = [];
    let distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    let distanceY = km / 110.574;

    let theta, x, y;
    for (let i = 0; i < points; i++) {
      theta = (i / points) * (2 * Math.PI);
      x = distanceX * Math.cos(theta);
      y = distanceY * Math.sin(theta);

      ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);

    return {
      type: "Polygon",
      coordinates: [ret]
    };
  }
});
